import React, { useEffect, useMemo } from 'react'
import { Button, TabMenu } from '@demandlocal/components'
import { Navigate, Route, Routes } from 'react-router-dom'
import Modal from '../../../components/Modal/Modal'
import { useNavigate, useParams } from 'react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { find } from 'lodash'
import { toast } from 'react-hot-toast'
import { usePostValidateEmail } from '../../../api/admin/usePostValidateEmail'
import { queryClient } from '../../../api/queryClient'
import EditUserInfo from '../../Admin/User/EditUserInfo'
import { useGetAgencyUser } from '../../../api/agencies/users/useGetUser'
import UserDealerships from './UserDealerships'
import { useGetUserDealerships } from '../../../api/agencies/users/useGetUserDealerships'
import { useGetDealershipsByOrganizationId } from '../../../api/admin/useGetDealershipsByOrganizationId'
import { useEditUser } from '../../../api/agencies/users/useEditUser'

export default function AdminUserEdit(): JSX.Element {
  const { user_id, agency_id } = useParams()
  const navigate = useNavigate()

  const formContext = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
  })

  const { data: user } = useGetAgencyUser(user_id)
  const { data: userDealerships } = useGetUserDealerships(user_id)
  const { mutateAsync: editUser } = useEditUser()
  const { mutateAsync: postValidateEmail } = usePostValidateEmail()

  const { data: allDealerships } = useGetDealershipsByOrganizationId(Number(agency_id))

  const userDealershipsIds = useMemo(
    () => userDealerships?.dealerships?.map((d) => d.dealership_id) ?? [],
    [userDealerships],
  )

  const rolesOptions = useMemo(
    () => [
      {
        label: 'Agency Admin',
        value: 2,
      },
      {
        label: 'Basic User',
        value: 3,
      },
    ],
    [],
  )

  const { reset, handleSubmit, setError, watch } = formContext
  const selectedRole = watch('role_id')?.value

  useEffect(() => {
    reset({
      ...user,
      role_id: find(rolesOptions, (role) => role.value === user?.role_id),

      organization: {
        has_all_dealerships_assigned: userDealerships?.has_all_dealership_access,
        assigned_dealerships_count: userDealerships?.has_all_dealership_access
          ? allDealerships?.length
          : allDealerships?.filter((d) => userDealershipsIds.includes(d.id))?.length,
        dealerships: allDealerships?.map((d) =>
          userDealershipsIds.includes(d.id) ? { ...d, checked: true } : d,
        ),
      },
    })
  }, [
    allDealerships,
    reset,
    rolesOptions,
    user,
    userDealerships?.dealerships,
    userDealerships?.has_all_dealership_access,
    userDealershipsIds,
  ])

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = 'unset'
    }
  }, [])

  const onSubmit = async (values) => {
    const role_id = Number(values?.role_id?.value ?? 3)
    const toastId = toast.loading('Saving user...')

    if (values.email !== user?.email) {
      const validateEmailResponse = await postValidateEmail({
        data: { email: values.email },
      })
      if (!validateEmailResponse?.valid) {
        toast.dismiss(toastId)
        return setError('email', {
          type: 'manual',
          message: 'This email is already in use.',
        })
      }
    }

    const dealerships = !values?.organization?.has_all_dealerships_assigned
      ? values?.organization?.dealerships
          .filter((dealership) => dealership.checked)
          .map((dealership) => ({
            dealership_id: dealership.id,
            organization_id: Number(agency_id),
          }))
          .flat()
      : []

    if (user?.id) {
      await editUser(
        {
          user_id: user?.id,
          data: {
            ...values,
            organization_id: Number(agency_id),
            has_all_dealerships_assigned:
              role_id === 2 || values?.organization?.has_all_dealerships_assigned,
            dealerships,
            role_id: values?.role_id?.valueOf().value,
          },
        },
        {
          onSuccess: () => {
            toast.success('User edited successfully', {
              id: toastId,
            })
            queryClient.refetchQueries([
              'agencies',
              'users',
              { pageIndex: 1, pageSize: 20, sortingParams: '' },
            ])
            navigate('../')
          },
          onError: (error: any) => {
            toast.dismiss(toastId)
            Object.entries(error?.response?.data ?? []).forEach(([key, value]) => {
              setError(key as keyof typeof user, { message: value as string })
            })
          },
        },
      )
    }
  }

  return (
    <Modal isOpen={true} key={'AgencyUserEdit'}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 flex-col h-screen relative'}>
          <div
            className={
              'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
            }>
            <div className={'text-3xl font-bold mt-12'}>
              Edit {user?.first_name} {user?.last_name}
            </div>
            <div className={'mt-12 mb-10'}>
              <TabMenu
                options={[
                  { name: 'Info', to: 'info' },
                  { name: 'Dealerships', to: 'dealerships', visible: selectedRole === 3 },
                ]}
              />
            </div>
            <div className={'font-Spartan overflow-scroll h-full'}>
              <Routes>
                <Route
                  path={'info'}
                  element={<EditUserInfo rolesOptions={rolesOptions} />}
                />
                <Route path={'dealerships'} element={<UserDealerships />} />

                <Route path={'*'} element={<Navigate to={'info'} />} />
              </Routes>
            </div>
            <div className={'flex justify-between py-5 border-t-2'}>
              <Button
                type={'button'}
                variant={'secondary'}
                onClick={() => {
                  navigate('../')
                }}>
                Cancel
              </Button>
              <Button variant={'primary'}>Save</Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
